"use client"

import { useState } from "react"
import { Card } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { useUserSettingsStore } from "@/hooks/stores/schoolSettingsStore"
import { Check } from "lucide-react"

interface NotificationSetting {
  key: keyof typeof DEFAULT_NOTIFICATIONS
  label: string
  description: string
}

const DEFAULT_NOTIFICATIONS = {
  emailNotifications: true,
  taskReminders: true,
  classUpdates: true,
  performanceAlerts: true,
}

const NOTIFICATION_SETTINGS: NotificationSetting[] = [
  {
    key: "emailNotifications",
    label: "Email Notifications",
    description: "Receive email updates about important school announcements and news",
  },
  {
    key: "taskReminders",
    label: "Task Reminders",
    description: "Get notified when you have upcoming tasks or assignments",
  },
  {
    key: "classUpdates",
    label: "Class Updates",
    description: "Receive notifications about class schedules and changes",
  },
  {
    key: "performanceAlerts",
    label: "Performance Alerts",
    description: "Get alerted when there are changes in your academic performance",
  },
]

export function NotificationsTab() {
  const { settings, updateNotifications } = useUserSettingsStore()
  const [notifications, setNotifications] = useState(settings.notifications)
  const [isSaved, setIsSaved] = useState(false)

  const handleToggle = (key: keyof typeof DEFAULT_NOTIFICATIONS) => {
    setNotifications((prev) => ({
      ...prev,
      [key]: !prev[key],
    }))
  }

  const handleSave = () => {
    updateNotifications(notifications)
    setIsSaved(true)
    setTimeout(() => setIsSaved(false), 3000)
  }

  return (
    <div className="space-y-6">
      <Card className="p-6 border border-border">
        <h3 className="text-lg font-semibold text-foreground mb-6">Notification Preferences</h3>

        <div className="space-y-4">
          {NOTIFICATION_SETTINGS.map((setting) => (
            <div
              key={setting.key}
              className="flex items-center justify-between p-4 border border-border rounded-lg hover:bg-muted/50 transition-colors"
            >
              <div className="flex-1">
                <h4 className="font-medium text-foreground">{setting.label}</h4>
                <p className="text-sm text-muted-foreground">{setting.description}</p>
              </div>
              <button
                onClick={() => handleToggle(setting.key)}
                className={`relative w-12 h-6 rounded-full transition-colors ${
                  notifications[setting.key] ? "bg-primary" : "bg-muted"
                }`}
              >
                <span
                  className={`absolute top-1 left-1 w-4 h-4 bg-white rounded-full transition-transform ${
                    notifications[setting.key] ? "translate-x-6" : "translate-x-0"
                  }`}
                />
              </button>
            </div>
          ))}
        </div>
      </Card>

      <div className="flex items-center gap-4">
        <Button onClick={handleSave} className="bg-primary hover:bg-primary/90 text-white">
          Save Preferences
        </Button>
        {isSaved && (
          <div className="flex items-center gap-2 text-primary text-sm">
            <Check className="w-4 h-4" />
            <span>Preferences saved successfully</span>
          </div>
        )}
      </div>
    </div>
  )
}
